import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Banknote,
  BarChart3,
  Box,
  CircleDollarSign,
  PieChart,
  PlayCircle,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import {
  getActivityFeed,
  getDashboardSummary,
  getPnlTimeseries,
  type ActivityEvent,
  type ActiveDeploymentSnapshot,
  type DashboardMode,
} from "../api/dashboard";
import PreopenCard from "../components/PreopenCard";
import Card from "../components/Card";
import Chip from "../components/Chip";
import DataTable from "../components/DataTable";
import EmptyState from "../components/EmptyState";
import EquityCurve from "../components/EquityCurve";
import KpiTile from "../components/KpiTile";
import MiniBarChart from "../components/MiniBarChart";
import Skeleton from "../components/Skeleton";
import StatusPill from "../components/StatusPill";
import ToggleGroup from "../components/ToggleGroup";
import {
  formatINR,
  formatInt,
  formatIst,
  formatRelative,
  signedClass,
} from "../lib/format";

export default function DashboardPage() {
  // Page-wide All / Paper / Live filter. Lives in the URL (?mode=paper) so it
  // survives a reload and every card below reads the same value.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawMode = searchParams.get("mode");
  const mode: DashboardMode | undefined =
    rawMode === "paper" || rawMode === "live" ? rawMode : undefined;
  function setMode(v: string) {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("mode", v);
    else next.delete("mode");
    setSearchParams(next, { replace: true });
  }

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["dashboard-summary", mode ?? "all"],
    queryFn: () => getDashboardSummary(mode),
    refetchInterval: 3_000,
  });

  const [tsBucket, setTsBucket] = useState<"1h" | "1d">("1d");
  const { data: ts } = useQuery({
    queryKey: ["pnl-timeseries", tsBucket, mode ?? "all"],
    queryFn: () =>
      getPnlTimeseries({
        bucket: tsBucket,
        ...(mode ? { mode } : {}),
      }),
    refetchInterval: 30_000,
  });

  const { data: activity } = useQuery({
    queryKey: ["activity-feed", mode ?? "all"],
    queryFn: () => getActivityFeed(40, mode),
    refetchInterval: 5_000,
  });

  // Tiles that split Live / Paper show only the selected mode's row.
  const showLive = mode !== "paper";
  const showPaper = mode !== "live";

  // Sparkline for the equity curve KPI tile — derive from the same series.
  const equitySpark = useMemo(
    () => (ts ?? []).map((p) => Number(p.total_pnl)),
    [ts],
  );

  const brokerBars = useMemo(
    () =>
      (summary?.broker_breakdown ?? []).map((r) => ({
        label: `${r.broker} / ${r.mode}`,
        value: Number(r.total_pnl),
      })),
    [summary],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Dashboard</h1>
          <p className="text-sm text-muted mt-0.5">
            {mode === "paper"
              ? "Paper deployments only."
              : mode === "live"
                ? "Live deployments only."
                : "Realtime view of your trading account."}
          </p>
        </div>
        <ToggleGroup
          value={mode ?? ""}
          onChange={setMode}
          options={[
            { value: "", label: "All" },
            { value: "paper", label: "Paper" },
            { value: "live", label: "Live" },
          ]}
        />
      </div>

      {/* Pre-open readiness. Above the numbers on purpose: before the open,
          whether you CAN trade matters more than yesterday's P&L. */}
      <PreopenCard />

      {/* KPI tiles */}
      {summaryLoading && !summary ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiTile
            label="Total P&L today"
            value={formatINR(summary?.kpis.total_pnl_today, { sign: true })}
            valueRaw={Number(summary?.kpis.total_pnl_today ?? 0)}
            tone="pnl"
            sub={
              <span>
                Realized{" "}
                <span className={signedClass(summary?.kpis.realized_today ?? 0)}>
                  {formatINR(summary?.kpis.realized_today, { sign: true })}
                </span>
              </span>
            }
            icon={<CircleDollarSign size={14} />}
            sparkline={equitySpark}
          />
          <KpiTile
            label="Realized (all time)"
            value={formatINR(summary?.kpis.realized_all_time, { sign: true })}
            valueRaw={Number(summary?.kpis.realized_all_time ?? 0)}
            tone="pnl"
            icon={
              Number(summary?.kpis.realized_all_time ?? 0) >= 0 ? (
                <TrendingUp size={14} />
              ) : (
                <TrendingDown size={14} />
              )
            }
          />
          <KpiTile
            label="Unrealized"
            value={
              <div className="space-y-1 text-lg">
                {showLive && (
                  <ModeRow
                    mode="Live"
                    main={
                      <span className={signedClass(Number(summary?.kpis.unrealized_live ?? 0))}>
                        {formatINR(summary?.kpis.unrealized_live, { sign: true })}
                      </span>
                    }
                    note={`invested ${formatINR(summary?.kpis.invested_live ?? 0)}`}
                  />
                )}
                {showPaper && (
                  <ModeRow
                    mode="Paper"
                    main={
                      <span className={signedClass(Number(summary?.kpis.unrealized_paper ?? 0))}>
                        {formatINR(summary?.kpis.unrealized_paper, { sign: true })}
                      </span>
                    }
                    note={`invested ${formatINR(summary?.kpis.invested_paper ?? 0)}`}
                  />
                )}
              </div>
            }
            icon={<Banknote size={14} />}
          />
          <KpiTile
            label="Open positions"
            value={
              <div className="space-y-1 text-lg">
                {showLive && (
                  <ModeRow mode="Live" main={formatInt(summary?.kpis.open_positions)} />
                )}
                {showPaper && (
                  <ModeRow mode="Paper" main={formatInt(summary?.kpis.open_positions_paper)} />
                )}
              </div>
            }
            icon={<Box size={14} />}
          />
          <KpiTile
            label="Deployments"
            value={formatInt(summary?.kpis.deployments_total)}
            sub={
              <span>
                {formatInt(summary?.kpis.running_deployments)} running
              </span>
            }
            icon={<Activity size={14} />}
            tone="brand"
          />
        </div>
      )}

      {/* Equity curve */}
      <Card
        title="Equity curve"
        subtitle="Cumulative P&L over time (IST). The final point includes live unrealized."
        actions={
          <div className="flex items-center gap-2 text-xs">
            <ToggleGroup
              value={tsBucket}
              onChange={(v) => setTsBucket(v as "1h" | "1d")}
              options={[
                { value: "1h", label: "Hourly" },
                { value: "1d", label: "Daily" },
              ]}
            />
          </div>
        }
      >
        <EquityCurve points={ts ?? []} />
      </Card>

      {/* Broker × mode breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="P&L by broker / mode" className="lg:col-span-2">
          <BrokerBreakdownTable rows={summary?.broker_breakdown ?? []} />
        </Card>
        <Card title="Visual" tone="muted">
          <MiniBarChart bars={brokerBars} height={220} />
        </Card>
      </div>

      {/* Active deployments strip */}
      <Card
        title="Active deployments"
        subtitle="Currently running runners. Snapshot refreshes every 3 seconds."
        actions={
          <Link
            to="/deployments"
            className="text-xs text-brand-600 dark:text-brand-400 hover:underline inline-flex items-center gap-1"
          >
            All deployments <ArrowRight size={12} />
          </Link>
        }
      >
        <ActiveDeploymentsStrip items={summary?.active_deployments ?? []} />
      </Card>

      {/* Recent activity */}
      <Card title="Recent activity" subtitle="Fills, rejections and runner errors across all your deployments.">
        <ActivityFeed events={activity ?? []} />
      </Card>
    </div>
  );
}

function BrokerBreakdownTable({
  rows,
}: {
  rows: NonNullable<ReturnType<typeof getDashboardSummary>> extends Promise<infer T>
    ? T extends { broker_breakdown: infer R }
      ? R
      : never
    : never;
}) {
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={<PieChart size={32} />}
        title="No P&L yet"
        message="Buckets appear here once a deployment takes its first fills, grouped by broker and paper/live mode."
      />
    );
  }
  const totals = rows.reduce(
    (acc, r) => ({
      realized: acc.realized + Number(r.realized_pnl),
      unrealized: acc.unrealized + Number(r.unrealized_pnl),
      total: acc.total + Number(r.total_pnl),
      deployments: acc.deployments + r.deployments,
      open_positions: acc.open_positions + r.open_positions,
    }),
    { realized: 0, unrealized: 0, total: 0, deployments: 0, open_positions: 0 },
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted bg-surface-2/60">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Broker</th>
            <th className="text-left px-3 py-2 font-medium">Mode</th>
            <th className="text-right px-3 py-2 font-medium">Deployments</th>
            <th className="text-right px-3 py-2 font-medium">Open</th>
            <th className="text-right px-3 py-2 font-medium">Realized</th>
            <th className="text-right px-3 py-2 font-medium">Unrealized</th>
            <th className="text-right px-3 py-2 font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={`${r.broker}-${r.mode}`}
              className="border-t border-border/60 hover:bg-surface-2/40"
            >
              <td className="px-3 py-2 font-mono">{r.broker}</td>
              <td className="px-3 py-2">
                <Chip tone={r.mode === "live" ? "warn" : "info"} mono>
                  {r.mode}
                </Chip>
              </td>
              <td className="px-3 py-2 text-right font-mono">{r.deployments}</td>
              <td className="px-3 py-2 text-right font-mono">{r.open_positions}</td>
              <td className={`px-3 py-2 text-right font-mono ${signedClass(r.realized_pnl)}`}>
                {formatINR(r.realized_pnl, { sign: true })}
              </td>
              <td className={`px-3 py-2 text-right font-mono ${signedClass(r.unrealized_pnl)}`}>
                {formatINR(r.unrealized_pnl, { sign: true })}
              </td>
              <td className={`px-3 py-2 text-right font-mono font-semibold ${signedClass(r.total_pnl)}`}>
                {formatINR(r.total_pnl, { sign: true })}
              </td>
            </tr>
          ))}
          <tr className="border-t border-border bg-surface-2/60 font-medium">
            <td className="px-3 py-2" colSpan={2}>
              Total
            </td>
            <td className="px-3 py-2 text-right font-mono">{totals.deployments}</td>
            <td className="px-3 py-2 text-right font-mono">{totals.open_positions}</td>
            <td className={`px-3 py-2 text-right font-mono ${signedClass(totals.realized)}`}>
              {formatINR(totals.realized, { sign: true })}
            </td>
            <td className={`px-3 py-2 text-right font-mono ${signedClass(totals.unrealized)}`}>
              {formatINR(totals.unrealized, { sign: true })}
            </td>
            <td className={`px-3 py-2 text-right font-mono ${signedClass(totals.total)}`}>
              {formatINR(totals.total, { sign: true })}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ActiveDeploymentsStrip({ items }: { items: ActiveDeploymentSnapshot[] }) {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<PlayCircle size={32} />}
        title="Nothing running right now"
        message="Start a deployment to see its runtime stats here."
        action={
          <Link
            to="/deployments"
            className="text-sm text-brand-600 dark:text-brand-400 hover:underline inline-flex items-center gap-1"
          >
            Go to deployments <ArrowRight size={14} />
          </Link>
        }
      />
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {items.map((d) => (
        <Link
          to={`/deployments/${d.id}`}
          key={d.id}
          className="block rounded border border-border bg-surface-2/40 hover:bg-surface-2 transition-colors p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-fg truncate" title={d.name}>
                {d.name}
              </div>
              <div className="text-xs text-muted mt-0.5 font-mono">{d.strategy_key}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Chip tone={d.mode === "live" ? "warn" : "info"} mono>
                {d.mode}
              </Chip>
              <StatusPill status={d.status} />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="text-muted uppercase tracking-wide text-[10px]">Unrealized</div>
              <div className={`font-mono font-semibold ${signedClass(d.unrealized_pnl)}`}>
                {formatINR(d.unrealized_pnl, { sign: true })}
              </div>
            </div>
            <div>
              <div className="text-muted uppercase tracking-wide text-[10px]">Open</div>
              <div className="font-mono font-semibold text-fg">{d.open_positions}</div>
            </div>
            <div>
              <div className="text-muted uppercase tracking-wide text-[10px]">Ticks</div>
              <div className="font-mono font-semibold text-fg">{formatInt(d.ticks_processed)}</div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function ActivityFeed({ events }: { events: ActivityEvent[] }) {
  if (events.length === 0) {
    return (
      <EmptyState
        icon={<BarChart3 size={32} />}
        title="Quiet so far"
        message="Fills, rejected orders and runner errors will stream in here as they happen."
      />
    );
  }
  return (
    <DataTable
      rows={events}
      rowKey={(e, i) => `${e.type}-${e.deployment_id}-${e.ts}-${i}`}
      empty={null}
      columns={[
        {
          key: "ts",
          header: "When",
          width: "180px",
          render: (e) => (
            <div className="text-fg-subtle">
              <div className="text-xs">{formatRelative(e.ts)}</div>
              <div className="text-[10px] text-muted font-mono">{formatIst(e.ts)}</div>
            </div>
          ),
        },
        {
          key: "type",
          header: "Type",
          width: "140px",
          render: (e) => <ActivityBadge event={e} />,
        },
        {
          key: "summary",
          header: "Detail",
          render: (e) => (
            <div className="min-w-0">
              <div className="text-fg truncate" title={e.summary}>
                {e.summary}
              </div>
              <div className="text-xs text-muted truncate">
                <Link
                  to={`/deployments/${e.deployment_id}`}
                  className="hover:underline"
                >
                  {e.deployment_name}
                </Link>
              </div>
            </div>
          ),
        },
        {
          key: "pnl_delta",
          header: "P&L Δ",
          align: "right",
          mono: true,
          width: "140px",
          render: (e) =>
            e.pnl_delta != null ? (
              <span className={signedClass(e.pnl_delta)}>
                {formatINR(e.pnl_delta, { sign: true })}
              </span>
            ) : (
              <span className="text-muted">—</span>
            ),
        },
      ]}
    />
  );
}

function ActivityBadge({ event }: { event: ActivityEvent }) {
  if (event.type === "fill") {
    const isBuy = event.side === "BUY";
    return (
      <Chip tone={isBuy ? "up" : "down"} mono>
        {isBuy ? "FILL BUY" : "FILL SELL"}
      </Chip>
    );
  }
  if (event.type === "order_rejected") {
    return (
      <Chip tone="warn">
        <span className="inline-flex items-center gap-1">
          <AlertTriangle size={10} /> REJECTED
        </span>
      </Chip>
    );
  }
  if (event.type === "admin_action") {
    return (
      <Chip tone="warn">
        <span className="inline-flex items-center gap-1">
          <AlertTriangle size={10} /> ADMIN
        </span>
      </Chip>
    );
  }
  return (
    <Chip tone="down">
      <span className="inline-flex items-center gap-1">
        <AlertTriangle size={10} /> ERROR
      </span>
    </Chip>
  );
}

/** A compact "Live / Paper" row inside a KPI tile: mode chip + value + note. */
function ModeRow({
  mode,
  main,
  note,
}: {
  mode: "Live" | "Paper";
  main: React.ReactNode;
  note?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted w-10 shrink-0">
        {mode}
      </span>
      <span className="font-mono font-semibold">{main}</span>
      {note && <span className="text-[10px] text-fg-subtle ml-auto">{note}</span>}
    </div>
  );
}
